/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useApolloClient } from '@apollo/client';
import * as React from 'react';
import { Constants } from '../../../common/Constants';
import { useSetListDataMutation } from '../../../data/client/mutations/SetListData.generated';
import { useSetModalDataMutation } from '../../../data/client/mutations/SetModalData.generated';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import { useModalDataQuery } from '../../../data/client/queries/ModalData.generated';
import { ListApprovalsDocument } from '../../../data/rest/queries/ListApprovals.generated';
import { ListESignApprovalsDocument } from '../../../data/rest/queries/ListESignApprovals.generated';
import {
    ApprovalCategory,
    ApprovalRequestType,
    ApprovalUserRole,
    ListData,
    ModalComponentType,
} from '../../../data/types.generated';
import { LogCategory, ScenarioName, StatusName, StepName } from '../../../logger';
import {
    getDefaultListData,
    getESignUserRoles,
    listApprovalsPathBuilder,
    listESignApprovalsPathBuilder,
} from '../../../utilities/ApolloRestUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { isTemplateEnabled } from '../../../utilities/Utils';
import { CreateRequest } from '../CreateRequest';
import { CreateRequestProps } from '../CreateRequest/CreateRequest.types';
import { CreateRequestSwitcher } from '../CreateRequestSwitcher';
import { CreateTemplateApproval } from '../CreateTemplateApproval';
import { CreateTemplateApprovalContainerProps } from '../CreateTemplateApproval/CreateTemplateApproval.container';
import { NewRequestPageEmptyState } from '../NewRequestPageEmptyState';
import { NewRequestPageEmptyStateProps } from '../NewRequestPageEmptyState/NewRequestPageEmptyState';
import { ViewDetails } from '../ViewDetails';
import { ViewDetailsProps } from '../ViewDetails/ViewDetails.container';
import { ViewDetailsV2 } from '../ViewDetailsV2';
import { ApprovalsModalComponent } from './ApprovalsModal.component';

export const ApprovalsModalContainer: React.FC = (): JSX.Element => {
    const { data } = useModalDataQuery();
    const { data: listData } = useListDataQuery();

    const host = getAppHost();
    const logger = host.getLogger();
    const ecsConfig = host.getEcsConfig();
    const loc = host.getLocale();

    // Reset list position on refetch completed
    const onCompleted = (): void => {
        window.dispatchEvent(new Event('resetApprovalsListPosition'));
    };
    const client = useApolloClient();

    const isEsign =
        listData.listData?.approvalType === ApprovalRequestType.ESign ||
        listData.listData?.approvalType === ApprovalRequestType.Docusign ||
        listData.listData?.approvalType === ApprovalRequestType.Syntex;

    // Use apollo client to refetch the list of approvals instead of lazy queries.
    // Apollo lazy queries triggered unwanted network requests.
    const updateListApprovals = React.useCallback((): void => {
        const variables = !isEsign
            ? {
                  userRole: listData.listData?.userRole ?? ApprovalUserRole.Approver,
                  category: listData.listData?.category ?? ApprovalCategory.NotSpecified,
                  isActive: listData.listData?.isActive ?? null,
                  additionalFilters: listData.listData?.additionalFilters ?? null,
                  flowEnvironment: listData.listData?.flowEnvironment ?? null,
                  approvalType: listData.listData?.approvalType ?? ApprovalRequestType.Basic,
                  extendedParameters: listData.listData?.extendedParameters ?? {},
                  pathBuilder: listApprovalsPathBuilder,
              }
            : {
                  search: listData.listData?.additionalFilters?.title ?? null,
                  approvalType: listData.listData?.approvalType ?? ApprovalRequestType.ESign,
                  orderBy: 'date desc',
                  top: Constants.PAGE_SIZE,
                  skip: 0,
                  UserRoles: getESignUserRoles(listData.listData?.userRole, listData.listData?.approvalType),
                  pathBuilder: listESignApprovalsPathBuilder,
              };

        client
            .query({
                query: !isEsign ? ListApprovalsDocument : ListESignApprovalsDocument,
                variables,
                fetchPolicy: 'network-only',
            })
            .then((_: any): void => onCompleted());
    }, [client, listData, isEsign]);

    const [setListDataMutation] = useSetListDataMutation();

    const [setModalData] = useSetModalDataMutation();
    const { isOpen, componentType, componentProps, size } = data?.modalData || {};
    const logCategory = LogCategory.ModalDialog;

    // On dismissing the modal, set the modal state
    const onDismiss = React.useCallback((): void => {
        logger.updateCommonProperty({ ApprovalId: '' });

        // end scenario of task module that is currently open.
        if (logger.findScenario(ScenarioName.ViewDetails)) {
            logger.stopScenario(ScenarioName.ViewDetails);
            logger.markScenarioComplete(ScenarioName.ViewDetails);
            logger.markScenario(ScenarioName.PersonalApp, StepName.ExitViewDetails, StatusName.NA);
        } else {
            logger.markScenarioComplete(ScenarioName.CreateTemplatedApproval);
            logger.markScenarioComplete(ScenarioName.CreateApprovals);
            logger.markScenario(ScenarioName.PersonalApp, StepName.ExitCreateApproval, StatusName.NA);
        }

        setModalData({
            variables: {
                isOpen: false,
                componentType: null,
                componentProps: null,
            },
        });
    }, [logger, setModalData]);

    // On dismissing view details, set the modal state and refetch approval list
    const onDismissViewDetails = React.useCallback((): void => {
        logger.logTraceInfo(logCategory, 'Closing the Details View Modal dialog');
        onDismiss();
        updateListApprovals();
    }, [onDismiss, updateListApprovals, logCategory, logger]);

    // On dismissing create task module, switch the sidebar to sent
    const onDismissCreateApproval = React.useCallback(
        (listDataOverride?: Partial<ListData>): void => {
            logger.logTraceInfo(logCategory, 'Closing the Create Approval Modal dialog');
            onDismiss();
            const approvalType = listData?.listData?.approvalType;

            const ownerListData = getDefaultListData({
                userRole: ApprovalUserRole.Owner,
                approvalType,
                ...listDataOverride,
            });

            setListDataMutation({
                variables: ownerListData,
            });

            // If no list API query parameter has changed, force an list update
            if (
                listData.listData?.userRole === ownerListData.userRole &&
                listData.listData?.category === ownerListData.category &&
                listData.listData?.isActive === ownerListData.isActive &&
                listData.listData?.additionalFilters === ownerListData.additionalFilters &&
                listData.listData?.approvalType === ownerListData.approvalType
            ) {
                updateListApprovals();
            }
        },
        [onDismiss, updateListApprovals, setListDataMutation, listData, logCategory, logger]
    );

    // Render modal content based on modal state
    const onRenderContent = React.useCallback((): JSX.Element => {
        switch (componentType) {
            case ModalComponentType.ViewDetails:
                const viewDetailsProps = {
                    ...componentProps,
                };
                return <ViewDetails {...(viewDetailsProps as ViewDetailsProps)} onDismiss={onDismissViewDetails} />;
            case ModalComponentType.ViewDetailsV2:
                return (
                    <ViewDetailsV2
                        {...componentProps}
                        updateList={updateListApprovals}
                        onDismiss={onDismissViewDetails}
                    />
                );
            case ModalComponentType.CreateTaskModule:
                return isTemplateEnabled() &&
                    ecsConfig.MicrosoftTeamApprovals.ShowNewRequestPageToggleButton &&
                    !(componentProps as CreateRequestProps)?.overrideRequestType ? (
                    <CreateRequestSwitcher {...componentProps} onDismiss={onDismissCreateApproval} />
                ) : (
                    <CreateRequest {...componentProps} onDismiss={onDismissCreateApproval} />
                );
            case ModalComponentType.CreateFromTemplateTaskModule:
                return (componentProps as CreateTemplateApprovalContainerProps)?.templateInstance ? (
                    <CreateTemplateApproval
                        {...(componentProps as CreateTemplateApprovalContainerProps)}
                        onDismiss={onDismissCreateApproval}
                    />
                ) : (
                    <NewRequestPageEmptyState
                        {...(componentProps as NewRequestPageEmptyStateProps)}
                        onDismiss={onDismiss}
                    />
                );
            default:
                return null;
        }
    }, [componentType, componentProps, onDismissViewDetails, onDismissCreateApproval, updateListApprovals]);

    return (
        <ApprovalsModalComponent
            isOpen={isOpen}
            onRenderContent={onRenderContent}
            onDismiss={onDismiss}
            loc={loc}
            size={size}
        />
    );
};
